import { isRecord, requestJson } from "./http.js";

/** What the confirm page shows about the extension that asked to sign in. */
export interface LinkInfo {
  readonly status: string;
  readonly client: string;
  readonly createdAt: number;
  readonly expiresAt: number;
  /** Must equal the key from the link fragment. */
  readonly publicKeyB64: string;
  readonly ipMasked: string;
  /** The extension asked from the same address this browser uses. */
  readonly sameNetwork: boolean;
  readonly deviceLabel: string;
  /** This installation already has a remembered device key; linking replaces it. */
  readonly existingKeyForDevice: boolean;
}

/** The extension's new session. The token goes straight to the crypto worker and nowhere else. */
export interface LinkApproval {
  readonly token: string;
  readonly expiresAt: number;
  readonly userId: string;
  readonly email: string;
  readonly saltB64: string;
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

export function linkInfoFrom(body: unknown): LinkInfo {
  if (!isRecord(body) ||
    typeof body["status"] !== "string" ||
    typeof body["client"] !== "string" ||
    typeof body["createdAt"] !== "number" ||
    typeof body["expiresAt"] !== "number" ||
    typeof body["publicKeyB64"] !== "string" ||
    typeof body["ipMasked"] !== "string" ||
    typeof body["sameNetwork"] !== "boolean" ||
    typeof body["deviceLabel"] !== "string" ||
    typeof body["existingKeyForDevice"] !== "boolean") {
    throw unexpected();
  }
  return {
    status: body["status"],
    client: body["client"],
    createdAt: body["createdAt"],
    expiresAt: body["expiresAt"],
    publicKeyB64: body["publicKeyB64"],
    ipMasked: body["ipMasked"],
    sameNetwork: body["sameNetwork"],
    deviceLabel: body["deviceLabel"],
    existingKeyForDevice: body["existingKeyForDevice"]
  };
}

function approvalFrom(body: unknown): LinkApproval {
  if (!isRecord(body) ||
    typeof body["token"] !== "string" ||
    typeof body["expiresAt"] !== "number" ||
    typeof body["userId"] !== "string" ||
    typeof body["email"] !== "string" ||
    typeof body["saltB64"] !== "string") {
    throw unexpected();
  }
  return { token: body["token"], expiresAt: body["expiresAt"], userId: body["userId"], email: body["email"], saltB64: body["saltB64"] };
}

function linkPath(id: string): string {
  return `/api/v1/extension-links/${encodeURIComponent(id)}`;
}

export async function fetchExtensionLink(id: string): Promise<LinkInfo> {
  return linkInfoFrom(await requestJson(linkPath(id)));
}

/** Needs a passkey check from the last minutes; the request layer asks for it (PASSKEY_REAUTH_REQUIRED). */
export async function approveExtensionLink(id: string, code: string): Promise<LinkApproval> {
  return approvalFrom(await requestJson(`${linkPath(id)}/approve`, { method: "POST", body: { code } }));
}

export async function deliverExtensionLink(id: string, sealedB64: string): Promise<void> {
  const body = await requestJson(`${linkPath(id)}/blob`, { method: "PUT", body: { sealedB64 } });
  if (!isRecord(body) || body["status"] !== "ready") {
    throw unexpected();
  }
}
